import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import type { QcChecklistSummary } from './contractsMirror.js';
import { useQcArchiveMutation, useQcDeleteMutation } from './useQc.js';

/**
 * Retiring a checklist, from the list.
 *
 * The list is where this belongs, because the job is nearly always plural — a run of trial
 * checklists, or a batch started against the wrong template. Making somebody open each one to get
 * rid of it is how the list stays full of them.
 *
 * TWO CONTROLS, NOT ONE, and the difference is the point:
 *
 *   Archive is reversible and offered on anything. It takes a checklist off the working list and
 *   changes nothing about what was inspected.
 *
 *   Delete destroys, and is offered ONLY while the checklist is unapproved. Sign-off is where a
 *   checklist becomes the document that proves a rough-in was inspected before it got covered, and
 *   the server answers 409 `checklist_approved` regardless of what is rendered here. Hiding the
 *   button is UX; the refusal is the guard.
 *
 * Both are gated on `qc.approve` by the caller, mirroring the api.
 */

export interface QcRowActionsProps {
  checklist: QcChecklistSummary;
}

export function QcRowActions({ checklist }: QcRowActionsProps) {
  const { t } = useTranslation();
  const archive = useQcArchiveMutation(checklist.id);
  const remove = useQcDeleteMutation(checklist.id);

  // Confirmation is a second press on the SAME control rather than a dialog. It works identically
  // on a phone, it needs no focus trap, and the destructive verb only ever appears once the person
  // has already reached for it.
  const [confirming, setConfirming] = useState(false);

  const archived = checklist.archivedAt !== null;
  // Mirrors the server's one refusal. An approved checklist can still be archived.
  const deletable = checklist.status !== 'approved';

  const failure = archive.error ?? remove.error;
  const errorMessage =
    failure instanceof ApiError
      ? t(`qc:errors.${failure.message}`, { defaultValue: t('qc:errors.generic') })
      : null;

  const busy = archive.isPending || remove.isPending;

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 px-4 pb-3 sm:px-2 sm:pb-0">
      {confirming ? (
        <>
          {/* The name is in the question because the row it sits beside is one of many, and a
              destructive confirm that does not say what it destroys is not a confirmation. */}
          <span className="text-destructive text-xs">
            {t('qc:manage.confirmDelete', { name: checklist.name })}
          </span>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={busy}
            onClick={() =>
              remove.mutate(undefined, {
                // Left open on failure: the row is still there, and so is the reason why.
                onSuccess: () => setConfirming(false),
              })
            }
          >
            {t('qc:manage.confirmDeleteAction')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            {t('qc:manage.cancel')}
          </Button>
        </>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => archive.mutate({ archived: !archived })}
          >
            {t(archived ? 'qc:manage.restore' : 'qc:manage.archive')}
          </Button>
          {deletable ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              {t('qc:manage.delete')}
            </Button>
          ) : null}
        </>
      )}

      {errorMessage ? (
        <p role="alert" className="w-full text-right text-destructive text-xs">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
